<template>
  <!--
    「요금·결제」 목록의 매장 체험 창(설계안 §25.4 — 「특정 매장을」) — 매장을 고르고(행이 아직 없는 매장도 — 서버가 행을 만든다) 체험 끝·사유를
    받아 지금부터 그 끝까지 체험하게 한다. 이용 중·지정 중인 매장은 서버가 409 `TRIAL_UNAVAILABLE` 로 거절한다(창 안에 서버 문장). 매장 칸·
    날짜 칸은 지정 창과 같은 부품이다. 말·요청은 `utils/subscriptionAdmin` 한 곳.
  -->
  <el-dialog
    :title="$t(text.storeTrial)"
    :visible="visible"
    width="460px"
    append-to-body
    data-testid="subscription-store-trial-dialog"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
    @open="reset">
    <p class="subscription-store-trial__intro">{{ $t(text.storeTrialIntro) }}</p>
    <el-form label-position="top" @submit.native.prevent>
      <SubscriptionStoreField
        :value="form.storeCd"
        :active="visible"
        :error="problems.storeCd"
        :new-store-notice="newStoreNotice"
        testid="subscription-store-trial"
        @change="pickStore" />
      <el-form-item :label="$t(text.columnTrialEnd)" :error="problems.picked">
        <SubscriptionMomentPicker
          v-model="form.picked"
          data-testid="subscription-store-trial-end"
          @change="problems.picked = ''" />
      </el-form-item>
      <el-form-item :label="$t(text.reason)" :error="problems.reason">
        <el-input
          v-model="form.reason"
          type="textarea"
          :rows="2"
          data-testid="subscription-store-trial-reason"
          :placeholder="$t(text.reasonPlaceholder)"></el-input>
      </el-form-item>
    </el-form>
    <p v-if="problem" class="subscription-store-trial__problem" data-testid="subscription-store-trial-problem">
      {{ problem }}
    </p>
    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ $t(text.close) }}</el-button>
      <el-button
        size="small"
        type="primary"
        data-testid="subscription-store-trial-submit"
        :disabled="!storeChosen"
        :loading="busy"
        @click="submit"
        >{{ $t(text.trialStart) }}</el-button
      >
    </span>
  </el-dialog>
</template>

<script>
import SubscriptionMomentPicker from '@/components/subscription/SubscriptionMomentPicker.vue'
import SubscriptionStoreField from '@/components/subscription/SubscriptionStoreField.vue'
import mainVueMixin from '@/mixins/mainVueMixin'
import { httpErrorMessage } from '@/plugins/http/policy'
import { SUBSCRIPTION_TEXT, SUBSCRIPTION_WRITE_CONFIG, isStoreCd, storeTrialRequestOf } from '@/utils/subscriptionAdmin'

const freshForm = () => ({ storeCd: null, picked: '', reason: '' })
const noProblems = () => ({ storeCd: '', picked: '', reason: '' })

export default {
  name: 'SubscriptionStoreTrialDialog',

  components: { SubscriptionMomentPicker, SubscriptionStoreField },

  mixins: [mainVueMixin],

  props: {
    visible: { type: Boolean, default: false },
  },

  data: function () {
    return {
      text: SUBSCRIPTION_TEXT,
      /**
       * 행 없는 매장(POS2 Lite 를 쓴 적 없음)을 골랐을 때의 경고 — 행이 생기고 체험이 끝나면(신청이 없으면) 플러스 기능이 닫힌다. [매장 지정]
       * 창에는 없다: 지정이 만드는 행은 체험이 비어 「시작 전」(기능 전부 열림)이라 닫히지 않는다
       */
      newStoreNotice: SUBSCRIPTION_TEXT.newStoreWarning,
      form: freshForm(),
      problems: noProblems(),
      /** 칸이 아닌 실패(서버 문장) */
      problem: '',
      busy: false,
    }
  },

  computed: {
    /** 매장을 골랐는가 — 고르기 전에는 실행 단추가 꺼져 있다(지정 창과 같은 규칙) */
    storeChosen: function () {
      return isStoreCd(this.form.storeCd)
    },
  },

  methods: {
    /** 열 때마다 새로 */
    reset: function () {
      this.form = freshForm()
      this.problems = noProblems()
      this.problem = ''
      this.busy = false
    },

    /** 선택기에서 골랐다(비우면 `null`) */
    pickStore: function (storeCd) {
      this.form.storeCd = storeCd
      this.problems.storeCd = ''
    },

    /**
     * 체험 시작 — 200 은 그 매장의 구독 상세다(`started` — 부모가 닫힌 창 뒤 목록을 다시 읽는다). 거절은 이 창 안에서 서버 문장 한 번 —
     * 409(`TRIAL_UNAVAILABLE`·`CHARGE_PENDING`·`STATUS_CHANGED` — 그 사이 다른 운영자가 지정·결제로 바꿨다)면 창·적은 값·문장은 그대로 두고
     * `changed` 로 부모가 목록만 다시 읽게 한다(상세 창의 409 와 같은 규칙).
     */
    submit: async function () {
      const request = storeTrialRequestOf(this.form, Date.now())
      this.problems = noProblems()
      this.problem = ''
      if (!request.ok) {
        this.problems = Object.assign(noProblems(), { [request.field]: this.$t(request.problem) })
        return
      }

      this.busy = true
      try {
        const response = await this.$axios.post(request.url, request.body, SUBSCRIPTION_WRITE_CONFIG)

        this.mainVue.$message({ type: 'success', message: this.$t(this.text.trialStartDone) })
        this.$emit('update:visible', false)
        this.$emit('started', response && response.data)
      } catch (error) {
        this.problem = httpErrorMessage(error, this.$t(this.text.writeFailed))
        if (error && error.status === 409) this.$emit('changed')
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.subscription-store-trial__intro {
  margin: 0 0 12px;
  color: var(--color-text-regular);
  word-break: keep-all;
}

.subscription-store-trial__problem {
  margin: 0;
  color: var(--color-danger);
}
</style>
